import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { createInquiry } from "@/lib/api";

const SERVICE_OPTIONS = [
  { value: "real_estate", label: "Real Estate Aerials" },
  { value: "watersports", label: "Boats & Watersports" },
  { value: "cinematic", label: "Cinematic Video" },
  { value: "other", label: "Something else" },
];

export const Booking = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    location: "",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service_type || !form.message) {
      toast.error("Please fill in name, email, service, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await createInquiry({
        ...form,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
      });
      setSuccess(true);
      toast.success("Inquiry sent. We'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        location: "",
        message: "",
      });
      setDate(null);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Could not send inquiry. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative py-24 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <span className="overline">§ 06 — Schedule</span>
            <h2 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Tell us about
              <br />
              <span className="text-[#E5A93B]">the shot.</span>
            </h2>
            <p className="mt-8 text-white/60 leading-relaxed font-light max-w-md">
              Send a few details and a preferred date. We&apos;ll reply within
              24 hours with availability, pricing, and next steps.
            </p>
            <div className="mt-10 space-y-4 border-t border-white/10 pt-8">
              <div>
                <div className="overline text-white/40">Email</div>
                <a
                  href="mailto:lake.anna.aqua.aerials@gmail.com"
                  data-testid="contact-email-link"
                  className="font-mono text-xs md:text-sm text-white hover:text-[#E5A93B] transition whitespace-nowrap"
                >
                  lake.anna.aqua.aerials@gmail.com
                </a>
              </div>
              <div>
                <div className="overline text-white/40">Service Area</div>
                <div className="font-mono text-sm text-white">
                  Lake Anna, Virginia · 10+ miles of lake
                </div>
              </div>
              <div>
                <div className="overline text-white/40">Turnaround</div>
                <div className="font-mono text-sm text-white">
                  24-72 hours typical
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-7">
            {success ? (
              <div
                data-testid="booking-success"
                className="border border-[#E5A93B]/40 bg-[#0D1322] p-10 md:p-14"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-2 w-2 bg-[#E5A93B] rounded-full animate-pulse" />
                  <span className="overline">REC · Message saved</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                  We got your message!
                </h3>
                <div className="mt-6 space-y-3 text-white/70 font-light leading-relaxed">
                  <p>
                    Thanks for reaching out — your inquiry is on file and{" "}
                    <span className="text-[#E5A93B]">
                      we&apos;ll personally reply within 24 hours
                    </span>{" "}
                    from{" "}
                    <span className="font-mono text-sm text-white">
                      lake.anna.aqua.aerials@gmail.com
                    </span>
                    .
                  </p>
                  <p className="text-white/50 text-sm">
                    If it&apos;s urgent, feel free to email us directly with the
                    same details — we check it daily.
                  </p>
                </div>

                <div className="mt-8 border-t border-white/10 pt-6 flex flex-wrap gap-3">
                  <a
                    href="mailto:lake.anna.aqua.aerials@gmail.com"
                    data-testid="booking-success-email"
                    className="inline-flex items-center gap-2 bg-[#E5A93B] hover:bg-[#FACC15] text-black px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
                  >
                    Email us directly
                  </a>
                  <button
                    data-testid="booking-reset"
                    onClick={() => setSuccess(false)}
                    className="inline-flex items-center gap-2 border border-white/20 text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-white/5"
                  >
                    Send another
                  </button>
                </div>

                <p className="mt-8 font-mono text-[10px] tracking-[0.25em] uppercase text-white/30">
                  // Aqua Aerials LKA · auto-reply v1
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="booking-form"
                className="bg-[#0D1322] border border-white/10 p-8 md:p-10 space-y-6"
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Name" required>
                    <Input
                      data-testid="form-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Your name"
                      className="bg-[#05080F] border-white/10 text-white rounded-none h-12 focus-visible:ring-[#E5A93B] focus-visible:ring-offset-0"
                    />
                  </Field>
                  <Field label="Email" required>
                    <Input
                      data-testid="form-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      placeholder="you@example.com"
                      className="bg-[#05080F] border-white/10 text-white rounded-none h-12 focus-visible:ring-[#E5A93B] focus-visible:ring-offset-0"
                    />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Phone (optional)">
                    <Input
                      data-testid="form-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      placeholder="(___) ___-____"
                      className="bg-[#05080F] border-white/10 text-white rounded-none h-12 focus-visible:ring-[#E5A93B] focus-visible:ring-offset-0"
                    />
                  </Field>
                  <Field label="Service" required>
                    <Select
                      value={form.service_type}
                      onValueChange={(v) => update("service_type", v)}
                    >
                      <SelectTrigger
                        data-testid="form-service"
                        className="bg-[#05080F] border-white/10 text-white rounded-none h-12 focus:ring-[#E5A93B] focus:ring-offset-0"
                      >
                        <SelectValue placeholder="Choose service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0D1322] border-white/10 text-white rounded-none">
                        {SERVICE_OPTIONS.map((o) => (
                          <SelectItem
                            key={o.value}
                            value={o.value}
                            data-testid={`form-service-${o.value}`}
                            className="focus:bg-[#161E33] focus:text-[#E5A93B]"
                          >
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Preferred date">
                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="form-date-trigger"
                          className={cn(
                            "h-12 w-full justify-start rounded-none bg-[#05080F] border-white/10 text-white hover:bg-[#161E33] hover:text-white",
                            !date && "text-white/40"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-[#E5A93B]" />
                          {date ? format(date, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="bg-[#0D1322] border-white/10 text-white rounded-none p-0"
                        align="start"
                      >
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={(d) => { setDate(d); setDateOpen(false); }}
                          disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                          initialFocus
                          className="bg-[#0D1322] text-white"
                        />
                      </PopoverContent>
                    </Popover>
                  </Field>
                  <Field label="Shoot location">
                    <Input
                      data-testid="form-location"
                      value={form.location}
                      onChange={(e) => update("location", e.target.value)}
                      placeholder="Address or cove name"
                      className="bg-[#05080F] border-white/10 text-white rounded-none h-12 focus-visible:ring-[#E5A93B] focus-visible:ring-offset-0"
                    />
                  </Field>
                </div>

                <Field label="Tell us about the shoot" required>
                  <Textarea
                    data-testid="form-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    rows={5}
                    placeholder="What are we shooting? Time of day? Anything special?"
                    className="bg-[#05080F] border-white/10 text-white rounded-none focus-visible:ring-[#E5A93B] focus-visible:ring-offset-0 resize-none"
                  />
                </Field>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit"
                    className="inline-flex items-center justify-center gap-3 bg-[#E5A93B] hover:bg-[#FACC15] disabled:opacity-50 text-black px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
                  >
                    {submitting && <Loader2 size={14} className="animate-spin" />}
                    {submitting ? "Sending…" : "Send Inquiry"}
                  </button>
                  <span className="font-mono text-[11px] tracking-wider uppercase text-white/40">
                    Reply within 24h · no spam
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="overline text-white/60">
      {label}
      {required && <span className="text-[#E5A93B]"> *</span>}
    </Label>
    {children}
  </div>
);
